import * as Track from "@axon-enterprise/track";
import { mixpanelTrack } from '@axon-enterprise/track';
import type { RegionName, TrackProfile } from '@axon-enterprise/track/dist/mixpanel/models/track';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { getAppConfig } from '../../utils/config';
import { DEFAULT_MIXPANEL_API_ENDPOINT } from '../../utils/constants';

import { mixpanelService } from './MixpanelService';


type TrackFn = (eventName: string, properties?: Record<string, unknown>) => void;
type SetUserProfileFn = (profile: TrackProfile) => Promise<void>;
type SetMetadataFn = (metadata: Record<string, unknown>) => void;

interface TrackingContextValue {
  track: TrackFn;
  setUserProfile: SetUserProfileFn;
  setMetadata: SetMetadataFn;
}

const TrackingContext = createContext<TrackingContextValue | null>(null);

export const useTracking = (): TrackingContextValue => {
  const ctx = useContext(TrackingContext);
  if (!ctx) {
    throw new Error('useTracking must be used within a MixpanelProvider');
  }
  return ctx;
};

export const MixpanelProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const location = useLocation();
  const config = getAppConfig();

  useEffect(() => {
    // Initialize once using Axon's track wrapper
    if (!config.enableAnalytics) return;
    const region = (config.region || 'unknown') as RegionName;
    const mxConfig: Record<string, unknown> = {};
    if (config.mixpanelApiHost && `${config.mixpanelApiHost}`.trim() !== '') {
      const endpoint = (config.mixpanelApiEndpoint && config.mixpanelApiEndpoint.trim() !== '') ? config.mixpanelApiEndpoint : DEFAULT_MIXPANEL_API_ENDPOINT;
      mxConfig.api_host = `https://${config.mixpanelApiHost}${endpoint}`;
    }
    (mixpanelTrack as any).init(config.appName, region, mxConfig);
    (mixpanelTrack as any).registerSafely({ [Track.MxPropertyName.FususRegionName]: config.environment });
    mixpanelService.init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Track page view on route change
    const path = location.pathname + location.search;
    const url = typeof window !== 'undefined' ? window.location.href : path;
    (mixpanelTrack as any).trackPageView({ url: url, pageName: path });
  }, [location.pathname, location.search]);

  const value = useMemo<TrackingContextValue>(
    () => ({
      track: (eventName, properties) => {
        (mixpanelTrack as any).trackSafely(eventName, properties);
      },
      setUserProfile: async (profile) => {
        await (mixpanelTrack as any).setProfile(profile);
      },
      setMetadata: (metadata) => {
        mixpanelService.setMetadata(metadata);
      },
    }),
    []
  );

  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
};

export default MixpanelProvider;


